import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
const accomplishments = ['Led the fight since 2018 to remove the farm worker exemption from minimum wage law', 'Expanded Medicaid to more than 400,000 Virginians', 'Secured the right for public workers to unionize', 'Helped raise the minimum wage', 'Passed paid sick leave', 'Banned ICE from cooperating with local law enforcement', 'Protected communities from harmful data center expansion'];
export function AboutPage() {
  return <div className="min-h-screen bg-warmgray font-sans text-navy selection:bg-red selection:text-white" data-id="element-201">
      <Navbar alwaysSolid data-id="element-202" />

      <main className="pt-28 pb-24" data-id="element-203">
        {/* Hero Section */}
        <section className="bg-navy py-20 md:py-32 px-6 md:px-12 text-center relative overflow-hidden" data-id="element-204">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-sky via-transparent to-transparent" data-id="element-205"></div>
          <div className="max-w-4xl mx-auto relative z-10" data-id="element-206">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-207">
              <h1 className="text-5xl md:text-7xl font-serif text-white mb-8 leading-tight" data-id="element-208">
                Meet <span className="italic text-sky" data-id="element-209">Elizabeth</span>
              </h1>
              <p className="text-xl text-white/60 font-medium tracking-wide max-w-2xl mx-auto mb-4" data-id="element-210">
                Mother. Social Worker. Union Sister. Immigrant.
              </p>
              <p className="text-xl text-white/60 font-medium tracking-wide max-w-2xl mx-auto mb-4 italic pl-8" data-id="element-211">
                Fighter for Working Families.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section 1: An Immigrant Story — Photo Left, Text Right */}
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-24" data-id="element-212">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-213">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-214">
              <div className="relative w-full" style={{
              paddingBottom: '110%'
            }} data-id="element-215">
                {/* Christmas photo — top right, behind family photo */}
                <div className="absolute top-0 right-0 w-[45%] aspect-[3/4] rounded-sm overflow-hidden shadow-xl z-10 rotate-[2deg] border-4 border-white" data-id="element-216">
                  <img src="https://cdn.magicpatterns.com/uploads/8UjKCG1Um1ze43WQa6rgKz/IMG_1361.jpg" alt="Young Elizabeth during the holidays" className="w-full h-full object-cover object-center" data-id="element-217" />
                </div>
                {/* Main family photo — top left, in front of Christmas photo */}
                <div className="absolute top-[8%] left-0 w-[65%] aspect-[3/2] rounded-sm overflow-hidden shadow-2xl z-20 border-4 border-white" data-id="element-218">
                  <img src="https://cdn.magicpatterns.com/uploads/u1BfLDdy9nm5eCEUS1rpwv/EG_about_website.jpg" alt="Elizabeth Guzmán with her family" className="w-full h-full object-cover object-center" data-id="element-219" />
                </div>
                {/* Daughter Christmas photo — bottom left, behind office photo */}
                <div className="absolute bottom-[5%] left-[0%] w-[60%] aspect-[4/3] rounded-sm overflow-hidden shadow-xl z-20 rotate-[2deg] border-4 border-white" data-id="element-220">
                  <img src="https://cdn.magicpatterns.com/uploads/gXUuwbztWSQguyDjbTjobc/IMG_1362.jpg" alt="Elizabeth in her early years" className="w-full h-full object-cover object-top" data-id="element-221" />
                </div>
                {/* Office/work photo — bottom right, in front of bottom left */}
                <div className="absolute bottom-[0%] right-[0%] w-[60%] aspect-[4/3] rounded-sm overflow-hidden shadow-xl z-30 rotate-[-2deg] border-4 border-white" data-id="element-222">
                  <img src="https://cdn.magicpatterns.com/uploads/u6DgBaRMW9zmeTunGuyy3k/IMG_1364.jpg" alt="Elizabeth at work" className="w-full h-full object-cover object-center" data-id="element-223" />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.15
          }} className="space-y-6 text-lg text-navy/80 font-light leading-relaxed" data-id="element-224">
              <div className="flex items-center gap-4 mb-2" data-id="element-225">
                <div className="h-[1px] w-12 bg-red" data-id="element-226"></div>
                <span className="text-red font-bold tracking-widest uppercase text-sm" data-id="element-227">
                  Her Story
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-navy leading-snug" data-id="element-228">
                $300 and a Dream
              </h2>
              <p data-id="element-229">
                Elizabeth Guzmán is a social worker, union member, and
                immigrant. She came to America from Peru as a young single
                mother with $300 in her pocket, working three minimum-wage jobs
                to afford a one-bedroom apartment.
              </p>
              <p data-id="element-230">
                She eventually worked her way through Northern Virginia
                Community College, earning two master's degrees — proving that
                the American Dream is possible when you fight for it.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section 2: A Life of Service — Text Left, Photo Right */}
        <section className="bg-white" data-id="element-231">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-24" data-id="element-232">
            <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-233">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="space-y-6 text-lg text-navy/80 font-light leading-relaxed order-2 md:order-1" data-id="element-234">
                <div className="flex items-center gap-4 mb-2" data-id="element-235">
                  <div className="h-[1px] w-12 bg-red" data-id="element-236"></div>
                  <span className="text-red font-bold tracking-widest uppercase text-sm" data-id="element-237">
                    Public Service
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif text-navy leading-snug" data-id="element-238">
                  A Life of Service
                </h2>
                <p data-id="element-239">
                  She has spent her career in local government and human
                  services, including protecting children from neglect and
                  abuse.
                </p>
                <p data-id="element-240">
                  Today, she oversees a $20 million budget and a 200-person
                  operations staff for the City of Alexandria's Department of
                  Adult Services, which serves some of our most vulnerable
                  populations.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: 0.15
            }} className="order-1 md:order-2" data-id="element-241">
                <div className="aspect-[4/5] w-full rounded-sm overflow-hidden shadow-2xl" data-id="element-242">
                  <img src="https://cdn.magicpatterns.com/uploads/23y4K5ay1aX7bzVe1Ui49n/Elizabeth_Guzman-Greeting_employees-9.jpg" alt="Elizabeth Guzmán greeting a local business employee" className="w-full h-full object-cover object-center" data-id="element-243" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Section 3: Making History — Photo Left, Text Right */}
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-24" data-id="element-244">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-245">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-246">
              <div className="aspect-[4/5] w-full rounded-sm overflow-hidden shadow-2xl" data-id="element-247">
                <img src="https://cdn.magicpatterns.com/uploads/iBaQkdwrwXE41kkvT2bamD/Making_Herstory.jpg" alt="Elizabeth Guzmán speaking in the Virginia General Assembly" className="w-full h-full object-cover object-center" data-id="element-248" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.15
          }} className="space-y-6 text-lg text-navy/80 font-light leading-relaxed" data-id="element-249">
              <div className="flex items-center gap-4 mb-2" data-id="element-250">
                <div className="h-[1px] w-12 bg-red" data-id="element-251"></div>
                <span className="text-red font-bold tracking-widest uppercase text-sm" data-id="element-252">
                  Legislative Record
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-navy leading-snug" data-id="element-253">
                Making History
              </h2>
              <p data-id="element-254">
                In 2017, Elizabeth made history as the first Latina immigrant
                elected to the Virginia General Assembly, flipping a
                Republican-held seat by 10 points — and then doing it again in
                2025. She introduced more than 100 bills during her time in
                office and served as vice chair of the education committee and
                chair of the subcommittee of the social services committee.
              </p>
              <p data-id="element-255">
                As a second-generation union member, she led the fight for a
                paid sick days law so no Virginian had to choose between their
                health and a paycheck. She also passed historic legislation
                lifting Virginia's blanket ban on public sector collective
                bargaining. She has been recognized by numerous advocacy
                organizations as one of the most effective lawmakers, including
                the Virginia Education Association and the Virginia League of
                Conservation Voters.
              </p>
              <p data-id="element-256">
                In 2018, Speaker Nancy Pelosi invited Elizabeth to deliver the
                Spanish-language rebuttal to President Donald Trump's State of
                the Union address — a testament to her national standing as a
                voice for immigrant and working-class communities.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Accomplishments Section */}
        <section className="bg-white py-24" data-id="element-257">
          <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-258">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-259">
              <span className="text-red font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-260">
                Track Record
              </span>
              <h2 className="text-3xl md:text-5xl font-serif text-navy" data-id="element-261">
                A Record of{' '}
                <span className="italic text-sky" data-id="element-262">Progressive Wins</span>
              </h2>
            </motion.div>

            <motion.ul initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="space-y-6" data-id="element-263">
              {accomplishments.map((item, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex items-start gap-4 text-lg text-navy/80 font-light" data-id="element-264">
                  <CheckCircle className="text-red shrink-0 mt-1" size={24} strokeWidth={1.5} data-id="element-265" />
                  <span data-id="element-266">{item}</span>
                </motion.li>)}
            </motion.ul>
          </div>
        </section>

        {/* Quote Section */}
        <section className="bg-navy py-20 px-6 md:px-12 text-center" data-id="element-267">
          <div className="max-w-3xl mx-auto" data-id="element-268">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-269">
              <p className="text-2xl md:text-3xl font-serif text-white leading-relaxed mb-8 italic" data-id="element-270">
                "My record speaks for itself: I've taken on tough fights, even
                when it meant standing up to my own party, because that's what
                it takes to deliver for working families. I'm the only candidate
                in this race with a proven record of delivering change. It's
                time to send someone to Congress who has been in this fight for
                decades."
              </p>
              <div className="flex items-center justify-center gap-4" data-id="element-271">
                <div className="w-12 h-[1px] bg-red" data-id="element-272"></div>
                <span className="text-white font-medium tracking-widest uppercase text-sm" data-id="element-273">
                  Delegate Elizabeth Guzmán
                </span>
                <div className="w-12 h-[1px] bg-red" data-id="element-274"></div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-6 md:px-12 text-center" data-id="element-275">
          <h2 className="text-3xl font-serif text-navy mb-6" data-id="element-276">
            Ready to join the fight?
          </h2>
          <p className="text-navy/70 mb-10 font-light text-lg" data-id="element-277">
            Stand with Elizabeth and help build a movement for working families.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-278">
            <a href="/volunteer" className="inline-block bg-red hover:bg-red-light text-white font-bold text-sm tracking-widest uppercase px-10 py-4 rounded-sm transition-colors shadow-md" data-id="element-279">
              Volunteer
            </a>
            <a href="https://secure.actblue.com/donate/guzman_va_web" target="_blank" rel="noopener noreferrer" className="inline-block bg-transparent border-2 border-navy hover:bg-navy hover:text-white text-navy font-bold text-sm tracking-widest uppercase px-10 py-4 rounded-sm transition-colors" data-id="element-280">
              Donate
            </a>
          </div>
        </section>
      </main>

      <Footer data-id="element-281" />
    </div>;
}